import { useEffect } from 'react'

import { useFlexOffersContext } from '@cox/core-ui8/dist/FlexOffersContext'
import { CustomerType, RuleType, VisitorType } from '@cox/core-ui8/dist/types'
import { useUdoContext } from '@cox/core-ui8/dist/UdoContext'
import axios from 'axios'

import RuleDetails from './RuleDetails'
import RuleTypeRenderer from './RuleTypeRenderer'
import { RuleBuilderProps } from './types'
import { isAuthor, WEB_APIS } from '../../../constants'
import { useInversionFlags } from '../../../hooks/useInversionFlags'
import { extractByPropertyNames } from '../../../utils/helper-util'
import { getOrderByFlexOffers, getPayLoadByOfferType, getPayloadByRuleExpression } from '../../../utils/rule-builder-util'

const RuleBuilder = (props: RuleBuilderProps): JSX.Element => {
  const {
    ruleId = '',
    ruleExpression = '',
    ruleDescription = '',
    isInEditor = false,
    editmode = false,
    ruleType = '',
    ruleName = '',
    resultsFalse,
    resultsTrue,
    defaultVariationContent,
    showDefaultContent = false,
    defaultVariationFragmentPath = '',
    trueExpFragmentPath = '',
    falseExpFragmentPath = '',
    componentTypeRenderer
  } = props
  const isEditor = isInEditor || editmode || isAuthor()
  const { udo } = useUdoContext()
  const { visitorType = '', pAddressCapture = '' } = udo
  const initialFlexOffers = {
    offers: [],
    discounts: [],
    cinfos: [],
    success: false,
    loading: false,
    error: false
  }
  const { setFlexOffersContext } = useFlexOffersContext()
  const { condition = '', ...ruleExpressionObject } = getPayloadByRuleExpression(ruleExpression)
  const payload = ruleExpression ? ruleExpressionObject : {}
  const { customerType, offerIds } = payload

  const isNoncustomer = () => {
    if (visitorType) {
      return customerType === CustomerType.PROSPECT && visitorType === VisitorType.NONCUSTOMER
    } else if (isInEditor) {
      return customerType === CustomerType.PROSPECT
    }
    return false
  }

  const isCustomer = () => {
    if (visitorType) {
      return (
        (customerType === CustomerType.PINPOINT || customerType === CustomerType.CUSTOMER) &&
        visitorType === VisitorType.CUSTOMER
      )
    } else if (isInEditor) {
      return customerType === CustomerType.PINPOINT || customerType === CustomerType.CUSTOMER
    }
    return false
  }

  const { inversionProperties, isLocal } = useInversionFlags()

  const fetchFlexOffers = async () => {
    if (ruleExpression && payload && ruleType === RuleType.pricing) {
      let response
      payload.isLocal = isLocal
      payload.inversionZipcode = isLocal ? inversionProperties?.pricing_zip : undefined
      const customPayload = getPayLoadByOfferType(payload)
      try {
        if (isNoncustomer()) {
          setFlexOffersContext({
            [ruleExpression]: { ...initialFlexOffers, loading: true }
          })
          if (pAddressCapture) {
            response = await axios.post(WEB_APIS.FLEX_OFFER_ADDRESS_API, customPayload, { withCredentials: true })
          } else {
            response = await axios.get(WEB_APIS.FLEX_OFFERS_API, { withCredentials: true, headers: customPayload })
          }
          const { data } = response
          const { offers, success, errorMessage } = data
          if (offers) {
            const processedFlexOffers = getOrderByFlexOffers(offers, offerIds)
            response.data.offers = processedFlexOffers
          }
          setFlexOffersContext({
            [ruleExpression]: { ...response.data, success: success, loading: false, error: errorMessage?.length > 0 }
          })
        } else if (isCustomer()) {
          setFlexOffersContext({
            [ruleExpression]: { ...initialFlexOffers, loading: true }
          })
          if (payload.getCurrentSubscription) {
            try {
              response = await axios.get(WEB_APIS.CURRENT_SUBSCRIPTION, { withCredentials: true })
              const { data } = response
              const { cinfo = {}, success = false } = data
              const cinfos = []
              if (Object.keys(cinfo).length > 0) {
                cinfos.push(cinfo)
              }
              setFlexOffersContext({
                [ruleExpression]: {
                  cinfos: cinfos,
                  offers: [],
                  discounts: [],
                  success: success,
                  loading: false,
                  error: false
                }
              })
            } catch (err) {
              console.error('🚀', err)
              setFlexOffersContext({
                [ruleExpression]: {
                  offers: [],
                  discounts: [],
                  cinfos: [],
                  success: false,
                  loading: false,
                  error: true
                }
              })
            }
          } else {
            response = await axios.post(WEB_APIS.FLEX_OFFERS_API, customPayload, { withCredentials: true })
            const { data } = response
            const { propositions = {}, success = false, errormessage = '', unChecked = {} } = data
            if (customerType === CustomerType.PINPOINT) {
              setFlexOffersContext({
                [ruleExpression]: { ...propositions, success: success, loading: false, error: errormessage?.length > 0 }
              })
              try {
                const customProperties = {
                  Type: 'PRESENTED',
                  DateTime: 'date_time'
                }
                const dispositionList = extractByPropertyNames(
                  (propositions as any)?.offers,
                  'DispositionList',
                  customProperties
                )
                const dispositionPayload = { DispositionList: dispositionList }
                await axios.post(WEB_APIS.SET_DISPOSITION, dispositionPayload, {
                  withCredentials: true
                })
              } catch (err) {
                console.error(err)
              }
            }
            if (customerType === CustomerType.CUSTOMER) {
              const { errorMessage, offers } = unChecked
              if (offers) {
                const processedFlexOffers = getOrderByFlexOffers(offers, offerIds)
                unChecked.offers = processedFlexOffers
              }
              setFlexOffersContext({
                [ruleExpression]: { ...unChecked, success: success, loading: false, error: errorMessage?.length > 0 }
              })
            }
          }
        } else {
          setFlexOffersContext({
            [ruleExpression]: { ...initialFlexOffers }
          })
        }
      } catch (error: any) {
        setFlexOffersContext({
          [ruleExpression]: { ...error, loading: false, error: true }
        })
        console.error(error)
      }
    }
  }

  useEffect(() => {
    fetchFlexOffers()
    return () => {
      setFlexOffersContext({})
    }
  }, [])

  return (
    <>
      <RuleDetails
        isInEditor={isEditor}
        ruleId={ruleId}
        ruleType={ruleType}
        ruleName={ruleName}
        ruleDescription={ruleDescription}
        ruleExpression={ruleExpression}
        trueExpFragmentPath={trueExpFragmentPath}
        falseExpFragmentPath={falseExpFragmentPath}
        showDefaultContent={showDefaultContent}
        defaultVariationFragmentPath={defaultVariationFragmentPath}
      />
      <RuleTypeRenderer
        ruleId={ruleId}
        resultsTrue={resultsTrue}
        resultsFalse={resultsFalse}
        defaultVariationContent={defaultVariationContent}
        showDefaultContent={showDefaultContent}
        ruleType={ruleType}
        condition={condition as string}
        ruleExpression={ruleExpression}
        isInEditor={isEditor}
        componentTypeRenderer={componentTypeRenderer}
        compName={props?.compName}
      />
    </>
  )
}
export default RuleBuilder
